"use client";

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiLogIn } from "react-icons/fi";
import { XIcon } from "./icons/XIcon";
import { useLoginModal } from "@/contexts/LoginModalContext";

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
}

export default function LoginPromptModal({
  isOpen,
  onClose,
  message = "Sign in to interact with posts",
}: LoginPromptModalProps) {
  const { openLoginModal } = useLoginModal();

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleSignIn = () => {
    onClose();
    openLoginModal();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white dark:bg-[#18181D] rounded-[5px] max-w-sm w-full p-6 border border-gray-200 dark:border-[#25252B] shadow-[0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-[#CCFF00] dark:hover:text-[#CCFF00] transition-all active:scale-90"
            >
              <XIcon size={20} />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#CCFF00]/10 border border-[#CCFF00]/20 rounded-[5px] flex items-center justify-center mx-auto mb-4">
                <FiLogIn className="w-8 h-8 text-[#CCFF00]" />
              </div>
              <h3 className="text-xl font-monument font-bold text-gray-900 dark:text-white mb-2">
                Sign in required
              </h3>
              <p className="text-gray-600 dark:text-[#A1A1AA] mb-6 font-inter-tight">{message}</p>

              <div className="space-y-3">
                <button
                  onClick={handleSignIn}
                  className="block w-full py-3 px-4 bg-[#CCFF00] text-[#0A0A0A] font-inter-tight font-bold rounded-[5px] hover:bg-[#00FF9D] hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Sign in
                </button>
                <button
                  onClick={onClose}
                  className="block w-full py-3 px-4 bg-gray-100 dark:bg-[#0F0F12] text-gray-700 dark:text-gray-300 font-inter-tight font-medium rounded-[5px] border border-gray-200 dark:border-[#25252B] hover:bg-gray-200 dark:hover:bg-gray-900 hover:border-[#CCFF00]/30 active:scale-[0.98] transition-all duration-200"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
